import { useNavigate } from "@tanstack/react-router";
import { BottomNav } from "@qota/ui";
import { markLateral } from "../lib/transitions";

/**
 * La barra in basso, in un posto solo.
 *
 * Era ricopiata in cinque schermate, e ogni modifica — l'azione sempre
 * presente, il filetto, una destinazione nuova — andava fatta cinque volte.
 * Cinque copie di una lista sono cinque occasioni perché una resti indietro.
 *
 * **Amici prima di Gruppi**: la domanda che si fa aprendo l'app è «con chi ho
 * conti in ballo», e le persone vengono prima dei contenitori in cui i conti
 * stanno.
 *
 * L'icona di Amici è la **chiocciola** e non delle persone: in Qota ci si
 * aggiunge per nickname, e `users` è già Gruppi. Due sagome di persone
 * affiancate nella stessa barra si distinguono solo guardandole bene, che è
 * esattamente ciò che in una barra non si fa.
 */
export type NavDestination = "amici" | "gruppi" | "attivita" | "profilo";

const DESTINATIONS = [
  { value: "amici", icon: "at-sign", label: "Amici", to: "/amici" },
  { value: "gruppi", icon: "users", label: "Gruppi", to: "/gruppi" },
  { value: "attivita", icon: "receipt", label: "Attività", to: "/attivita" },
  { value: "profilo", icon: "user", label: "Profilo", to: "/profilo" },
] as const;

export function AppNav({
  value,
  onAdd,
  addLabel = "Nuova spesa",
}: {
  value: NavDestination;
  onAdd?: () => void;
  addLabel?: string;
}) {
  const navigate = useNavigate();

  return (
    <BottomNav
      items={DESTINATIONS.map(({ value: destination, icon, label }) => ({
        value: destination,
        icon,
        label,
      }))}
      value={value}
      onChange={(next) => {
        const target = DESTINATIONS.find((destination) => destination.value === next);
        // Toccare la destinazione in cui si è già non deve rimontare la
        // schermata: si perderebbero lo scorrimento e i fogli aperti.
        if (!target || target.value === value) return;

        // Le destinazioni della barra sono affiancate, non annidate: farle
        // scivolare direbbe una gerarchia che non esiste.
        markLateral();
        void navigate({ to: target.to });
      }}
      onAdd={onAdd}
      addLabel={addLabel}
    />
  );
}
